/**
 * Single navigation registry — drives both the sidebar and the route table, so
 * they can never drift. Mirrors the six backend domains.
 *
 * Each item carries an optional `group` for sidebar section dividers. The
 * shape of NavItem and the contents of NAV_ITEMS are otherwise unchanged.
 */
export type NavGroupId = 'overview' | 'setup' | 'modules' | 'admin';

export interface NavItem {
  key: string;
  label: string;
  path: string;
  description: string;
  group: NavGroupId;
}

export const NAV_GROUPS: readonly { id: NavGroupId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'setup', label: 'Setup' },
  { id: 'modules', label: 'Modules' },
  { id: 'admin', label: 'Admin' },
] as const;

export const NAV_ITEMS: readonly NavItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/',
    description: 'Platform overview',
    group: 'overview',
  },
  {
    key: 'connectors',
    label: 'Data Sources',
    path: '/connectors',
    description: 'Connect databases & upload files',
    group: 'setup',
  },
  {
    key: 'catalog',
    label: 'Data Catalogue',
    path: '/catalog',
    description: 'Schema inventory & business glossary',
    group: 'modules',
  },
  {
    key: 'quality',
    label: 'Data Quality',
    path: '/quality',
    description: 'Profiling & anomaly detection',
    group: 'modules',
  },
  {
    key: 'mdm',
    label: 'Master Data',
    path: '/mdm',
    description: 'Identity resolution & golden records',
    group: 'modules',
  },
  {
    key: 'domains',
    label: 'Data Domains',
    path: '/data-domains',
    description: 'Semantic column classification (EDC)',
    group: 'modules',
  },
  {
    key: 'chat',
    label: 'AI Assistant',
    path: '/chat',
    description: 'Ask questions about your connected data',
    group: 'modules',
  },
  {
    key: 'reporting',
    label: 'Reporting',
    path: '/reporting',
    description: 'Self-service charts & reports',
    group: 'modules',
  },
  {
    key: 'scheduler',
    label: 'Scheduler',
    path: '/scheduler',
    description: 'Scheduled data-profiling jobs',
    group: 'setup',
  },
  // Observability, Privacy & Security, Data Marketplace, and Diagnostics are
  // intentionally hidden from the sidebar and dashboard for now. Their routes
  // still exist in router.tsx, so re-adding the entries here restores them.
] as const;
